import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import QRCode from 'qrcode.react';
import api from '../../utils/api';

const Container = styled.div`
  padding: 20px;
  animation: fadeIn 0.5s ease;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 30px;
  padding-bottom: 15px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
  
  [data-theme='dark'] & {
    border-bottom: 1px solid rgba(255, 255, 255, 0.06);
  }
`;

const Title = styled.h1`
  margin: 0;
  color: var(--text-primary);
  font-size: 1.8rem;
  font-weight: 600;
`;

const FormContainer = styled.div`
  background-color: var(--paper-color);
  border-radius: var(--border-radius);
  padding: 30px;
  box-shadow: var(--box-shadow);
  margin-bottom: 40px;
  transition: all 0.3s ease;
  
  &:hover {
    box-shadow: 0 10px 20px rgba(0, 0, 0, 0.08);
  }
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const InputGroup = styled.div`
  margin-bottom: 20px;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 10px;
  font-weight: 500;
  color: var(--text-primary);
  font-size: 0.95rem;
`;

const Input = styled.input`
  width: 100%;
  padding: 14px 16px;
  border: 1px solid #e1e1e1;
  border-radius: 8px;
  font-size: 16px;
  transition: all 0.3s;
  background-color: #f9f9f9;
  color: var(--text-primary);
  font-family: 'Poppins', sans-serif;

  &:focus {
    outline: none;
    border-color: var(--primary-color);
    background-color: white;
    box-shadow: 0 0 0 4px rgba(67, 97, 238, 0.1);
  }
  
  &::placeholder {
    color: #aaa;
  }
  
  [data-theme='dark'] & {
    background-color: #2d2d2d;
    border-color: #444;
    color: var(--text-primary);
    
    &:focus {
      background-color: #333;
      border-color: var(--primary-color);
      box-shadow: 0 0 0 4px rgba(67, 97, 238, 0.2);
    }
    
    &::placeholder {
      color: #777;
    }
  }
`;

const Button = styled.button`
  background: linear-gradient(to right, var(--primary-color), var(--primary-dark));
  color: white;
  border: none;
  border-radius: 8px;
  padding: 14px 24px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s;
  box-shadow: 0 4px 8px rgba(67, 97, 238, 0.2);
  align-self: flex-start;

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 6px 12px rgba(67, 97, 238, 0.3);
  }

  &:active {
    transform: translateY(-1px);
  }

  &:disabled {
    background: #bdbdbd;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
`;

const QrCodesContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  gap: 25px;
`;

const QrCodeCard = styled.div`
  background-color: var(--paper-color);
  border-radius: var(--border-radius);
  padding: 25px;
  box-shadow: var(--box-shadow);
  display: flex;
  flex-direction: column;
  align-items: center;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  animation: fadeIn 0.5s ease;
  
  &:hover {
    transform: translateY(-8px);
    box-shadow: 0 12px 20px rgba(0, 0, 0, 0.1);
  }
`;

const QrCodeWrapper = styled.div`
  background-color: white;
  padding: 15px;
  border-radius: 12px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.05);
  margin-bottom: 15px;
  
  [data-theme='dark'] & {
    background-color: white; /* Keep QR code background white for readability */
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
  }
`;

const QrCodeLabel = styled.div`
  margin: 15px 0;
  font-weight: 600;
  text-align: center;
  color: var(--text-primary);
  font-size: 1.1rem;
`;

const QrCodeDesc = styled.div`
  margin: 15px 0;
  font-weight: 200;
  text-align: center;
  color: var(--text-secondary);
  font-size: 0.9rem;
  word-break: break-word;
  overflow-wrap: anywhere;
  a {
    color: var(--primary-color);
    text-decoration: underline;
  }
`;

const DownloadButton = styled.button`
  background-color: transparent;
  color: var(--primary-color);
  border: 2px solid var(--primary-color);
  border-radius: 8px;
  padding: 10px 20px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background-color: var(--primary-color);
    color: white;
    transform: translateY(-2px);
  }
  
  svg {
    margin-right: 8px;
  }
`;

const NoQrCodes = styled.div`
  text-align: center;
  padding: 60px 30px;
  background-color: var(--paper-color);
  border-radius: var(--border-radius);
  color: var(--text-secondary);
  margin-top: 20px;
  box-shadow: var(--box-shadow);
  font-size: 1.1rem;
  
  svg {
    width: 80px;
    height: 80px;
    margin-bottom: 20px;
    color: var(--text-light);
  }
`;

const SectionTitle = styled.h2`
  color: var(--text-primary);
  font-size: 1.5rem;
  font-weight: 600;
  margin: 0 0 25px 0;
  display: flex;
  align-items: center;
  
  svg {
    margin-right: 10px;
    color: var(--primary-color);
  }
`;

const ErrorMessage = styled.div`
  color: var(--error-color);
  margin-top: 10px;
  padding: 10px 15px;
  background-color: rgba(239, 71, 111, 0.1);
  border-radius: 8px;
  font-weight: 500;
  animation: fadeIn 0.3s ease;
`;

const SuccessMessage = styled.div`
  color: var(--success-color);
  margin-top: 10px;
  padding: 10px 15px;
  background-color: rgba(6, 214, 160, 0.1);
  border-radius: 8px;
  font-weight: 500;
  animation: fadeIn 0.3s ease;
`;

interface QrCode {
  id: string;
  token: string;
  visitorName: string;
  createdAt: string;
}

// Icon components
const HistoryIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M13 3C8.03 3 4 7.03 4 12H1L4.89 15.89L4.96 16.03L9 12H6C6 8.13 9.13 5 13 5C16.87 5 20 8.13 20 12C20 15.87 16.87 19 13 19C11.07 19 9.32 18.21 8.06 16.94L6.64 18.36C8.27 19.99 10.51 21 13 21C17.97 21 22 16.97 22 12C22 7.03 17.97 3 13 3ZM12 8V13L16.28 15.54L17 14.33L13.5 12.25V8H12Z" fill="currentColor"/>
  </svg>
);

const DownloadIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M19 9H15V3H9V9H5L12 16L19 9ZM5 18V20H19V18H5Z" fill="currentColor"/>
  </svg>
);

const EmptyQrCodeIcon = () => (
  <svg width="80" height="80" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M9.5 6.5V9.5H6.5V6.5H9.5ZM11 5H5V11H11V5ZM9.5 14.5V17.5H6.5V14.5H9.5ZM11 13H5V19H11V13ZM17.5 6.5V9.5H14.5V6.5H17.5ZM19 5H13V11H19V5ZM13 13H14.5V14.5H13V13ZM14.5 14.5H16V16H14.5V14.5ZM16 13H17.5V14.5H16V13ZM13 16H14.5V17.5H13V16ZM14.5 17.5H16V19H14.5V17.5ZM16 16H17.5V17.5H16V16ZM17.5 14.5H19V16H17.5V14.5ZM17.5 17.5H19V19H17.5V17.5ZM22 7H20V4H17V2H22V7ZM22 22V17H20V20H17V22H22ZM2 22H7V20H4V17H2V22ZM2 2V7H4V4H7V2H2Z" fill="currentColor"/>
  </svg>
);

const GenerateQrCode: React.FC = () => {
  const [visitorName, setVisitorName] = useState('');
  const [loading, setLoading] = useState(false);
  const [qrCodes, setQrCodes] = useState<QrCode[]>([]);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  // Load previously generated QR codes on component mount
  useEffect(() => {
    const fetchQrCodes = async () => {
      try {
        const response = await api.get('/qrcodes', {
          params: { limit: 12, page: 1 }
        });
        
        if (response.data && response.data.visitors) {
          setQrCodes(response.data.visitors);
        }
      } catch (err) {
        console.error('Error fetching QR codes:', err);
      }
    };
    
    fetchQrCodes();
  }, []);

  // URL for QR code
  const baseUrl = window.location.origin;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccessMessage('');
    setLoading(true);

    try {
      console.log('Generating QR code for:', visitorName);
      const response = await api.post('/qrcodes', {
        visitorName
      });

      console.log('QR code generation response:', response.data);
      
      // Add the new QR code to the list
      setQrCodes([response.data, ...qrCodes]);
      
      // Show success message
      setSuccessMessage(`QR code for ${visitorName} was successfully generated!`);
      
      // Clear the form
      setVisitorName('');
    } catch (err) {
      console.error('Error generating QR code:', err);
      setError('Failed to generate QR code. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const downloadQrCode = (id: string, visitorName: string) => {
    const canvas = document.getElementById(`qr-code-${id}`) as HTMLCanvasElement;
    if (canvas) {
      const url = canvas.toDataURL('image/png');
      const link = document.createElement('a');
      link.href = url;
      link.download = `qr-code-${visitorName.replace(/[^a-z0-9]/gi, '-').toLowerCase()}.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  return (
    <Container>
      <Header>
        <Title>Generate QR Codes</Title>
      </Header>

      <FormContainer>
        <Form onSubmit={handleSubmit}>
          <InputGroup>
            <Label htmlFor="visitorName">Visitor Name</Label>
            <Input
              type="text"
              id="visitorName"
              value={visitorName}
              onChange={(e) => setVisitorName(e.target.value)}
              required
              placeholder="Enter visitor name"
              autoFocus
            />
          </InputGroup>

          <div>
            <Button type="submit" disabled={loading}>
              {loading ? 'Generating...' : 'Generate QR Code'}
            </Button>
            {error && <ErrorMessage>{error}</ErrorMessage>}
            {successMessage && <SuccessMessage>{successMessage}</SuccessMessage>}
          </div>
        </Form>
      </FormContainer>

      <SectionTitle>
        <HistoryIcon />
        Generated QR Codes
      </SectionTitle>
      
      {qrCodes.length === 0 ? (
        <NoQrCodes>
          <EmptyQrCodeIcon />
          <div>No QR codes generated yet. Create your first QR code above!</div>
        </NoQrCodes>
      ) : (
        <QrCodesContainer>
          {qrCodes.map((qrCode) => (
            <QrCodeCard key={qrCode.id}>
              <QrCodeWrapper>
                <QRCode
                  id={`qr-code-${qrCode.id}`}
                  value={`${baseUrl}/gallery/${qrCode.token}`}
                  size={200}
                  level="H"
                  includeMargin
                  renderAs="canvas"
                  bgColor="#FFFFFF"
                  fgColor="#000000"
                />
              </QrCodeWrapper>
              <QrCodeLabel>{qrCode.visitorName}</QrCodeLabel>
              <QrCodeDesc>
                <a
                  href={`${baseUrl}/gallery/${qrCode.token}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {`${baseUrl}/gallery/...${qrCode.token.slice(-6)}`}
              </a>
              </QrCodeDesc>
              <DownloadButton
                type="button"
                onClick={() => downloadQrCode(qrCode.id, qrCode.visitorName)}
              >
                <DownloadIcon />
                Download QR Code
              </DownloadButton>
            </QrCodeCard>
          ))}
        </QrCodesContainer>
      )}
    </Container>
  );
};

export default GenerateQrCode;
